import { Injectable, DOCUMENT, inject } from '@angular/core';

import { UntypedFormGroup } from '@angular/forms';
import { Subject } from 'rxjs';

import { ModalService } from '../modal.service';
import { DiscardChangesModalComponent } from '../../components/modals/discard-changes-modal/discard-changes-modal.component';

@Injectable({
  providedIn: 'root',
})
export class FormService {
  private readonly document = inject<Document>(DOCUMENT);
  private readonly modalService = inject(ModalService);

  save = new Subject<number>();
  saveComplete = new Subject<void>();

  private savesInProgress = 0;
  private saveError = false;
  private forms: any = {};
  private grids: any = {};

  scrollToFirstInvalidField() {
    this.saveError = true;

    setTimeout(() => {
      const invalidDiv: NodeListOf<Element> = Array.prototype.slice
        .call(document.querySelectorAll('formly-field .invalid-feedback'))
        .filter((e: HTMLElement) => window.getComputedStyle(e.closest('formly-field')).display !== 'none')
        .filter((e: HTMLElement) => e.offsetParent && e.querySelector('formly-validation-message').textContent);

      if (invalidDiv && invalidDiv.length > 0) {
        const formlyField = invalidDiv[0].closest('formly-field');
        const invalidInput =
          formlyField.querySelector('input') || formlyField.querySelector('.form-control.ng-invalid');

        if (invalidInput) {
          invalidInput.focus();
          const invalidElementTop = invalidInput.getBoundingClientRect().top;
          if (invalidElementTop > 0) {
            const scrollTop = this.document.documentElement.scrollTop + invalidElementTop - 70;
            window.scrollTo({ top: scrollTop, behavior: 'smooth' });
          }
        }
      }
    }, 200);
  }

  get anySavesInProgress(): boolean {
    return this.savesInProgress > 0 || this.saveError;
  }

  isNewModel(model: any) {
    return !model?.id || model.id === '00000000-0000-0000-0000-000000000000';
  }

  beginSave() {
    this.saveError = false;
    ++this.savesInProgress;
  }

  endSave(error: boolean) {
    if (error) {
      this.saveError = true;
    }
    if (--this.savesInProgress === 0 && !this.saveError) {
      this.saveComplete.next();
    }
  }

  markAsSaved() {
    this.saveComplete.next();
  }

  addForm(modalLevel: number, formId: string, form: UntypedFormGroup) {
    if (!this.forms[modalLevel]) {
      this.forms[modalLevel] = {};
    }
    this.forms[modalLevel][formId] = form;
  }

  removeForm(modalLevel: number, formId: string) {
    delete this.forms[modalLevel][formId];
  }

  addGrid(modalLevel: number, gridId: string, grid: any) {
    if (!this.grids[modalLevel]) {
      this.grids[modalLevel] = {};
    }
    this.grids[modalLevel][gridId] = grid;
  }

  removeGrid(modalLevel: number, gridId: string) {
    delete this.grids[modalLevel][gridId];
  }

  resetForms(modalLevel: number) {
    this.forms[modalLevel] = {};
    this.grids[modalLevel] = {};
  }

  anyFormChanges(modalLevel: number) {
    return (
      (this.forms[modalLevel] && Object.values(this.forms[modalLevel]).some((form: UntypedFormGroup) => form.dirty)) ||
      (this.grids[modalLevel] && Object.values(this.grids[modalLevel]).some((grid: any) => grid.isEdited))
    );
  }

  checkForChanges(modalLevel: number): Promise<boolean> {
    return this.anyFormChanges(modalLevel)
      ? this.modalService
          .openMedium<DiscardChangesModalComponent>(DiscardChangesModalComponent)
          .modal.result.then((canCopy: boolean) => {
            if (canCopy) {
              this.resetForms(modalLevel);
            }
            return canCopy;
          })
      : Promise.resolve(true);
  }
}
